import { redirect } from "@tanstack/react-router";
import { isImpersonating } from "@/features/impersonation/impersonation-session";
import { getSecondFactorVerified } from "./mfa";

/**
 * Client-side 2FA gate, called from protected route guards. If the user is
 * required to use 2FA but the current session has not completed a verified
 * second factor, this throws a redirect to the MFA setup route.
 *
 * This is a UX gate only. The API independently rejects first-factor-only
 * requests from mfa-required users (FORBIDDEN: MFA_REQUIRED), which is the real
 * security boundary. See docs/two-factor-auth-enforcement-design.md.
 */
export const enforceMfaGate = async (
  mfaRequired: boolean,
  pathname: string,
): Promise<void> => {
  // While impersonating, `mfaRequired` is the target's flag. The API checked
  // the staff member's own 2FA requirement when authorizing the impersonation.
  if (!mfaRequired || isImpersonating()) {
    return;
  }

  // getSecondFactorVerified fails closed, so a transient error redirects to
  // setup rather than letting the user through (the API is the backstop).
  if (!(await getSecondFactorVerified())) {
    throw redirect({
      to: "/auth/mfa",
      search: { redirect: pathname },
    });
  }
};
